// src/hooks/useScheduleDragAndDrop.js
import { useState, useCallback, useEffect, useMemo } from 'react';
import {
    parseTime,
    formatTimeFromMinutes,
    getUniqueRooms,
    getWeekNumber,
} from '../utils/helpers.ts';
import {
    SCHEDULE_START_MINUTE,
    SCHEDULE_END_MINUTE,
    DRAG_CONFIG,
} from '../config/constants';
import { useActiveDays } from './useActiveDays';
import ScheduleEntry from '../models/ScheduleEntry.ts';
import { availabilityService } from '../services/AvailabilityService.ts';
import { conflictManager } from '../services/ConflictManager.ts';
import { createEmptyDraggedEntry } from '../types/dragAndDrop.ts';
import { mergeConflictPeerEntries } from '../utils/conflictPeers.ts';

export const KEY_SEPARATOR = '|';

export function useScheduleDragAndDrop(baseScheduleItems, setBaseScheduleItems, user, addLogEntry, teachers, groups, subjects, rooms = [], options = {}) {
    // Get active days from AssetManager
    const { activeDays: defaultActiveDays } = useActiveDays();
    const {
        autoApplyToAllWeeks = false,
        currentDate,
        activeDays = defaultActiveDays,
        timelineContext,
        conflictPeerEntries = []
    } = options;
    const [draggedItemInfo, setDraggedItemInfo] = useState(createEmptyDraggedEntry());
    const [availabilityOverlayData, setAvailabilityOverlayData] = useState(null);
    
    // State for the week change confirmation modal
    const [pendingMoveData, setPendingMoveData] = useState(null);
    const [isWeekConfirmationModalOpen, setIsWeekConfirmationModalOpen] = useState(false);
    const [isWaitingForConfirmation, setIsWaitingForConfirmation] = useState(false); // Flag to prevent cleanup
    
    const isAdmin = user?.role === 'Admin';

    const resolvedTimelineContext = useMemo(() => {
        const fallbackDateCandidate = timelineContext?.date || currentDate;
        const fallbackDate = (() => {
            if (fallbackDateCandidate instanceof Date && !isNaN(fallbackDateCandidate.getTime())) {
                return fallbackDateCandidate;
            }
            if (typeof fallbackDateCandidate === 'string' || typeof fallbackDateCandidate === 'number') {
                const parsed = new Date(fallbackDateCandidate);
                if (!isNaN(parsed.getTime())) {
                    return parsed;
                }
            }
            return new Date();
        })();

        const derivedWeekNumber = timelineContext?.weekNumber ?? getWeekNumber(fallbackDate);
        const derivedYear = timelineContext?.year ?? fallbackDate.getFullYear();

        return {
            date: fallbackDate,
            weekNumber: derivedWeekNumber,
            year: derivedYear
        };
    }, [timelineContext, currentDate]);

    const contextWeekNumber = resolvedTimelineContext.weekNumber;
    const contextYear = resolvedTimelineContext.year;

    const scheduleItemsForConflictChecks = useMemo(() => (
        mergeConflictPeerEntries(
            (baseScheduleItems || []).filter((item) => item?.isScheduled?.()),
            conflictPeerEntries
        )
    ), [baseScheduleItems, conflictPeerEntries]);

    const buildConflictPeerEntries = useCallback((draftEntries) => (
        mergeConflictPeerEntries(
            (draftEntries || []).filter((item) => item?.isScheduled?.()),
            conflictPeerEntries
        )
    ), [conflictPeerEntries]);

    // Helper function to resolve room ID to room object
    const resolveRoomInfo = useCallback((roomId) => {
        if (!roomId || typeof roomId !== 'number') {
            return { name: '', id: undefined };
        }

        // Find matching room by ID
        const matchingRoom = rooms.find(room => room.id === roomId);

        if (matchingRoom) {
            return {
                name: matchingRoom.name || matchingRoom.roomCode || '',
                id: matchingRoom.id
            };
        }

        // If no matching room found, log error
        console.error(`[useScheduleDragAndDrop] No matching room found for ID: ${roomId}`);
        return { name: '', id: undefined };
    }, [rooms]);

    const handleGlobalDragStart = useCallback((scheduleEntry, clickOffsetY = 0) => {
        console.log("[useScheduleDragAndDrop] Starting drag for ScheduleEntry:", scheduleEntry);
        
        if (!scheduleEntry) {
            console.warn("[useScheduleDragAndDrop] Drag start ignored - ScheduleEntry is missing");
            return;
        }
        
        // Ensure we have a ScheduleEntry instance
        const entryInstance = scheduleEntry instanceof ScheduleEntry ? scheduleEntry : new ScheduleEntry(scheduleEntry);

        let durationMinutes = 0;
        if (typeof entryInstance.parseTime === 'function') {
            const parseResult = entryInstance.parseTime();
            durationMinutes = parseResult?.durationMinutes;
            
            if (durationMinutes == null || durationMinutes <= 0) {
                console.warn("[useScheduleDragAndDrop] Drag start ignored - invalid duration for ScheduleEntry", entryInstance);
                return;
            }
        } else {
            console.warn("[useScheduleDragAndDrop] Drag start ignored - ScheduleEntry has no valid time property or parseTime method", entryInstance);
            return;
        }
        
        console.log("[useScheduleDragAndDrop] Drag Start:", entryInstance.id, "Duration:", durationMinutes);
        
        const dragInfo = {
            entry: entryInstance,
            clickOffsetY: clickOffsetY || 0,
            durationMinutes: durationMinutes,
            typeDnd: entryInstance.typeDnd || 'scheduled_item',
            allowedDays: entryInstance.allowedDays || activeDays
        };

        // Hover handlers may run immediately after drag state is published.
        // Build the index first so optimized checks never see an empty index.
        availabilityService.buildSpatialIndexForWeek(
            scheduleItemsForConflictChecks,
            contextWeekNumber,
            contextYear
        );

        console.log("[useScheduleDragAndDrop] Setting draggedItemInfo:", dragInfo);
        setDraggedItemInfo(dragInfo);
    }, [activeDays, scheduleItemsForConflictChecks, contextWeekNumber, contextYear]);

    // Handler for when a drag operation ends globally
    const handleGlobalDragEnd = useCallback(() => {
        console.log("[useScheduleDragAndDrop] Drag End");
        setDraggedItemInfo(createEmptyDraggedEntry());
        setAvailabilityOverlayData(null);
        availabilityService.clearSpatialIndex();
        
        // Only clean up modal state if we're not waiting for confirmation
        if (!isWaitingForConfirmation) {
            console.log("[useScheduleDragAndDrop] Cleaning up modal state");
            setPendingMoveData(null);
            setIsWeekConfirmationModalOpen(false);
        } else {
            console.log("[useScheduleDragAndDrop] Preserving modal state - waiting for user confirmation");
        }
    }, [isWaitingForConfirmation]);

    // Calculate the broad heatmap progressively. The hovered slot uses the same
    // index immediately; background shading can arrive without blocking input.
    useEffect(() => {
        if (isAdmin && draggedItemInfo.entry && baseScheduleItems) {
            console.log(`[useScheduleDragAndDrop] Calculating availability overlay for dragged item ${draggedItemInfo.entry.id} (optimized)`);

            const draggedItemObject = draggedItemInfo.entry; // This is now a ScheduleEntry instance
            if (!(draggedItemObject instanceof ScheduleEntry)) {
                console.error("[useScheduleDragAndDrop] Dragged item object is not a ScheduleEntry instance for overlay calculation.", draggedItemObject);
                return;
            }

            const overlayWeekNumber = contextWeekNumber;
            const overlayYear = contextYear;
            const startTime = performance.now();
            const newOverlayData = new Map();
            const roomsToCheck = getUniqueRooms(scheduleItemsForConflictChecks);
            const daysToCheck = draggedItemInfo.allowedDays || activeDays;
            const checkIntervalMinutes = DRAG_CONFIG.timeSnapIntervalMinutes;

            const checks = [];
            daysToCheck.forEach(dayName => {
                roomsToCheck.forEach(roomName => {
                    for (let timeMinutes = SCHEDULE_START_MINUTE; timeMinutes < SCHEDULE_END_MINUTE; timeMinutes += checkIntervalMinutes) {
                        checks.push({ dayName, roomName, timeMinutes });
                    }
                });
            });

            let nextCheckIndex = 0;
            let animationFrameId = null;
            let cancelled = false;
            const frameBudgetMs = 4;

            const processNextChunk = () => {
                if (cancelled) return;

                const chunkStartedAt = performance.now();
                let checksProcessed = 0;
                while (
                    nextCheckIndex < checks.length
                    && (checksProcessed === 0 || performance.now() - chunkStartedAt < frameBudgetMs)
                ) {
                    const { dayName, roomName, timeMinutes } = checks[nextCheckIndex];
                    const availability = availabilityService.checkAvailabilityUltraOptimized(
                        draggedItemObject,
                        roomName,
                        dayName,
                        timeMinutes,
                        checkIntervalMinutes,
                        scheduleItemsForConflictChecks,
                        teachers,
                        groups,
                        subjects,
                        overlayWeekNumber,
                        overlayYear
                    );
                    if (availability.overallStatus !== 'green') {
                        const key = `${dayName}${KEY_SEPARATOR}${roomName}${KEY_SEPARATOR}${timeMinutes}`;
                        newOverlayData.set(key, availability);
                    }
                    nextCheckIndex += 1;
                    checksProcessed += 1;
                }

                if (nextCheckIndex < checks.length) {
                    animationFrameId = window.requestAnimationFrame(processNextChunk);
                    return;
                }

                setAvailabilityOverlayData(newOverlayData);
                const endTime = performance.now();
                console.log(`[useScheduleDragAndDrop] Overlay data calculated with ${newOverlayData.size} unavailable slots in ${endTime - startTime}ms (progressive)`);
            };

            animationFrameId = window.requestAnimationFrame(processNextChunk);

            return () => {
                cancelled = true;
                if (animationFrameId != null) {
                    window.cancelAnimationFrame(animationFrameId);
                }
            };
        } else if (!draggedItemInfo.entry) {
            console.log(`[useScheduleDragAndDrop] Clearing availability overlay data.`);
            setAvailabilityOverlayData(null);
            // Clear spatial index when drag ends
            availabilityService.clearSpatialIndex();
        }
    }, [draggedItemInfo, isAdmin, baseScheduleItems, scheduleItemsForConflictChecks, teachers, groups, subjects, activeDays, contextWeekNumber, contextYear]);

    // Function to actually apply the move after confirmation
    const applyScheduleMove = useCallback((itemId, targetRoomId, targetDay, newStartTimeString, applicationMode = 'allWeeks') => {
        console.log(`[useScheduleDragAndDrop] Applying schedule move: ID=${itemId}, RoomId=${targetRoomId}, Day=${targetDay}, Time=${newStartTimeString}, Mode=${applicationMode}`);

        // Get the original item snapshot (stored before any modifications)
        const originalItemSnapshot = pendingMoveData?.originalItem || draggedItemInfo.entry;
        
        if (!originalItemSnapshot) {
            console.error(`[useScheduleDragAndDrop] Cannot apply move: Original item snapshot not found`);
            return;
        }

        // Find the current live item in the schedule to apply changes to
        const rawLiveItem = baseScheduleItems.find(item => item && String(item.id) === String(itemId));
        let liveItem;
        let isAddingUnscheduledItem = false;
        
        if (!rawLiveItem) {
            // This might be an unscheduled item being moved to the schedule
            console.log(`[useScheduleDragAndDrop] Item ${itemId} not found in schedule, creating from original snapshot`);
            liveItem = originalItemSnapshot instanceof ScheduleEntry ? originalItemSnapshot : new ScheduleEntry(originalItemSnapshot);
            isAddingUnscheduledItem = true;
        } else {
            // Ensure we have a proper ScheduleEntry instance
            liveItem = rawLiveItem instanceof ScheduleEntry ? rawLiveItem : new ScheduleEntry(rawLiveItem);
        }

        const { startMinutes: newStartMinutes } = parseTime(newStartTimeString);
        if (newStartMinutes === null) {
            console.error(`[useScheduleDragAndDrop] Cannot apply move: Invalid time format ${newStartTimeString}`);
            return;
        }

        const newEndMinutes = newStartMinutes + originalItemSnapshot.durationMinutes;
        const newTimeEnd = formatTimeFromMinutes(newEndMinutes);

        const conflictWeekNumber = pendingMoveData?.currentWeekNumber ?? contextWeekNumber;
        const conflictYear = pendingMoveData?.currentYear ?? contextYear;

        if (applicationMode === 'thisWeekOnly') {
            console.log(`[useScheduleDragAndDrop] Creating week-specific exception for week ${conflictWeekNumber}`);

            // Resolve room ID to room object
            const roomInfo = resolveRoomInfo(targetRoomId);

            // Create the exception for this week only
            const exception = {
                week: conflictWeekNumber,
                year: conflictYear,
                changes: {
                    day: targetDay,
                    room: roomInfo.name,
                    room_id: roomInfo.id,
                    timeStart: newStartTimeString,
                    timeEnd: newTimeEnd
                }
            };

            // Add the exception to the live item
            const updatedItem = liveItem.addException(exception);
            
            // Update the schedule with the modified item
            setBaseScheduleItems(prevItems => {
                const updatedItems = prevItems.map(item => item.id === liveItem.id ? updatedItem : item);
                // Recalculate conflicts for the moved entry and affected entries
                conflictManager.recalculateConflictsForChangedEntries(
                    [updatedItem.id],
                    updatedItems,
                    teachers,
                    groups,
                    subjects,
                    { peerEntries: buildConflictPeerEntries(updatedItems) }
                ).then(entriesWithUpdatedConflicts => {
                    setBaseScheduleItems(entriesWithUpdatedConflicts);
                });
                return updatedItems;
            });

            // Log the change with the original snapshot vs updated item
            addLogEntry('modify', originalItemSnapshot, updatedItem);

        } else {
            console.log(`[useScheduleDragAndDrop] Applying move to all weeks in series`);
            
            // Resolve room ID to room object
            const roomInfo = resolveRoomInfo(targetRoomId);
            
            // Apply to all weeks - modify the base properties
            // Use toObject() to ensure all properties including weekSeries are preserved
            const baseProperties = liveItem instanceof ScheduleEntry ? liveItem.toObject() : liveItem;
            const updatedItem = new ScheduleEntry({
                ...baseProperties,
                day: targetDay,
                room: roomInfo.name,
                room_id: roomInfo.id,
                timeStart: newStartTimeString,
                timeEnd: newTimeEnd
            });

            // Update the schedule
            if (isAddingUnscheduledItem) {
                // Add the item to the schedule
                setBaseScheduleItems(prevItems => {
                    const updatedItems = [...prevItems, updatedItem];
                    // Recalculate conflicts for the new entry and affected entries
                    conflictManager.recalculateConflictsForChangedEntries(
                        [updatedItem.id],
                        updatedItems,
                        teachers,
                        groups,
                        subjects,
                        { peerEntries: buildConflictPeerEntries(updatedItems) }
                    ).then(entriesWithUpdatedConflicts => {
                        setBaseScheduleItems(entriesWithUpdatedConflicts);
                    });
                    return updatedItems;
                });
                console.log(`[useScheduleDragAndDrop] Added unscheduled item ${itemId} to schedule`);
            } else {
                // Update existing item in the schedule
                setBaseScheduleItems(prevItems => {
                    const updatedItems = prevItems.map(item => String(item.id) === String(liveItem.id) ? updatedItem : item);
                    // Recalculate conflicts for the moved entry and affected entries
                    conflictManager.recalculateConflictsForChangedEntries(
                        [updatedItem.id],
                        updatedItems,
                        teachers,
                        groups,
                        subjects,
                        { peerEntries: buildConflictPeerEntries(updatedItems) }
                    ).then(entriesWithUpdatedConflicts => {
                        setBaseScheduleItems(entriesWithUpdatedConflicts);
                    });
                    return updatedItems;
                });
                console.log(`[useScheduleDragAndDrop] Updated existing item ${itemId} in schedule`);
            }

            // Log the change with the original snapshot vs updated item
            addLogEntry(isAddingUnscheduledItem ? 'create' : 'modify', originalItemSnapshot, updatedItem);
        }

        // Clear pending move data
        setPendingMoveData(null);
    }, [pendingMoveData, draggedItemInfo, baseScheduleItems, setBaseScheduleItems, addLogEntry, parseTime, formatTimeFromMinutes, resolveRoomInfo, teachers, groups, subjects, contextWeekNumber, contextYear, buildConflictPeerEntries]);

    // Handler for when an item is dropped onto a valid target
    const handleItemDrop = useCallback((itemId, targetRoomId, targetDay, newStartTimeString) => {
        console.log(`[useScheduleDragAndDrop] Item Drop: ID=${itemId}, RoomId=${targetRoomId}, Day=${targetDay}, Time=${newStartTimeString}`);

        let draggedItem = draggedItemInfo.entry
            || baseScheduleItems.find((item) => item && String(item.id) === String(itemId));
        if (draggedItem && !(draggedItem instanceof ScheduleEntry)) {
            draggedItem = new ScheduleEntry(draggedItem);
        }
        if (!(draggedItem instanceof ScheduleEntry)) {
            console.error(`[useScheduleDragAndDrop] Drop failed: Dragged item data is invalid or missing.`);
            handleGlobalDragEnd();
            return;
        }

        // --- Conflict Checking before proceeding ---
        const { startMinutes: newStartMinutes } = parseTime(newStartTimeString);
        if (newStartMinutes === null) {
            console.error(`[useScheduleDragAndDrop] Drop failed: Could not parse new start time.`);
            handleGlobalDragEnd();
            return;
        }

        const { durationMinutes } = draggedItem.parseTime();
        // Get room name for availability checking (we need the room name for the service)
        const roomInfo = resolveRoomInfo(targetRoomId);
        const targetRoomName = roomInfo.name;
        
        const availability = availabilityService.checkAvailabilityUltraOptimized(
            draggedItem,
            targetRoomName,
            targetDay,
            newStartMinutes,
            durationMinutes,
            scheduleItemsForConflictChecks,
            teachers,
            groups,
            subjects,
            contextWeekNumber,
            contextYear
        );

        if (isAdmin && availability.overallStatus === 'red') {
            const conflictDetails = availability.conflictReasons
                .map((reason) => (
                    reason.isCrossPeriod
                        ? `${reason.reason} [other period]`
                        : reason.reason
                ))
                .join('\n - ');
            const confirmationMessage = `Warning: Dropping here has conflicts!\nReasons:\n - ${conflictDetails}\n\nProceed anyway?`;
            if (!window.confirm(confirmationMessage)) {
                console.log(`[useScheduleDragAndDrop] Drop cancelled by user due to conflict.`);
                handleGlobalDragEnd();
                return;
            }
            console.warn(`[useScheduleDragAndDrop] User confirmed drop despite conflict.`);
        }
        // --- End of Conflict Checking ---
        
        // Determine the ID of the root schedule item.
        // If it's an instance or exception, it will have an originalId.
        // Fallback to parsing the ID if originalId is not present.
        const rootItemId = draggedItem.additionalProps?.originalId || String(draggedItem.id).split('-w')[0];

        // Find the definitive root item from the main schedule list.
        // For unscheduled items being moved to schedule, use the draggedItem itself
        let rootItem = baseScheduleItems.find(item => item && String(item.id) === String(rootItemId));
        
        if (!rootItem) {
            // This might be an unscheduled item being moved to the schedule
            // In this case, use the draggedItem as the root item
            console.log(`[useScheduleDragAndDrop] Item ${rootItemId} not found in baseScheduleItems, treating as unscheduled item`);
            rootItem = draggedItem;
        }

        const isMultiWeekItem = rootItem.weekSeries && (rootItem.weekSeries.includes(',') || rootItem.weekSeries.includes('-'));

        if (isMultiWeekItem && !autoApplyToAllWeeks) {
            console.log("[useScheduleDragAndDrop] Multi-week item detected, showing confirmation modal.");
            setIsWaitingForConfirmation(true);
            
            // CRITICAL: Store a deep copy of the original item state BEFORE any modifications
            // This ensures change tracking can see the true "before" state
            const originalItemSnapshot = rootItem.clone();

            setPendingMoveData({
                itemId: rootItem.id,
                targetRoomId,
                targetDay,
                newStartTimeString,
                originalItem: originalItemSnapshot, // Store the unmodified snapshot
                currentWeekNumber: contextWeekNumber,
                currentYear: contextYear,
            });
            setIsWeekConfirmationModalOpen(true);
        } else {
            console.log(`[useScheduleDragAndDrop] Single-week item or auto-apply, applying move directly.`);
            applyScheduleMove(rootItem.id, targetRoomId, targetDay, newStartTimeString, 'allWeeks');
            handleGlobalDragEnd();
        }
    }, [
        draggedItemInfo.entry,
        isAdmin,
        baseScheduleItems,
        scheduleItemsForConflictChecks,
        teachers,
        groups,
        subjects,
        autoApplyToAllWeeks,
        handleGlobalDragEnd,
        applyScheduleMove,
        contextWeekNumber,
        contextYear
    ]);

    // Handler for week confirmation modal
    const handleWeekConfirmationClose = useCallback(() => {
        console.log("[useScheduleDragAndDrop] User cancelled week confirmation");
        setIsWeekConfirmationModalOpen(false);
        setPendingMoveData(null);
        setIsWaitingForConfirmation(false); // Reset flag
        handleGlobalDragEnd();
    }, [handleGlobalDragEnd]);

    const handleWeekConfirmationConfirm = (applicationMode) => {
        if (pendingMoveData) {
            const { itemId, targetRoomId, targetDay, newStartTimeString } = pendingMoveData;
            applyScheduleMove(itemId, targetRoomId, targetDay, newStartTimeString, applicationMode);
        }
        setIsWeekConfirmationModalOpen(false);
        setPendingMoveData(null);
        setIsWaitingForConfirmation(false); // Reset flag
        // Note: don't call handleGlobalDragEnd here since drag cleanup already happened
    };

    return {
        draggedItemInfo,
        availabilityOverlayData,
        dragConflictEntries: scheduleItemsForConflictChecks,
        handleGlobalDragStart,
        handleGlobalDragEnd,
        handleItemDrop,
        KEY_SEPARATOR,
        // Week confirmation modal state and handlers
        isWeekConfirmationModalOpen,
        pendingMoveData,
        handleWeekConfirmationClose,
        handleWeekConfirmationConfirm,
    };
}
